import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import PublicHeader from '../components/PublicHeader';
import Seo from '../components/Seo';
import {
  Assignment,
  Engineering,
  VerifiedUser,
  HomeOutlined,
  CheckCircle,
  Check,
  Add,
  Search,
  PhotoCamera,
  Lock,
} from '@mui/icons-material';

/* ───────────────────────────────────────────────────────────────────────
   HOMEPAGE — faithful port of docs/design/eqaya_homepage.html
   Exact reference palette + Figtree, with approved improvements:
   real icons (no emoji), a product mock below the hero, semantic links,
   a11y contrast fixes, and live route wiring. The previous homepage is
   preserved at HomePageLegacy.js.
   ─────────────────────────────────────────────────────────────────────── */

// Reference palette (matches the HTML exactly)
// Brand palette (landing page): Deep Teal #185451, Sage Grey #9FA8A3,
// Metallic Charcoal #5E6366 — plus white and light tints derived from them.
// Token keys are unchanged so existing usages pick up the new colors; the
// previous blue/amber accents are remapped onto the palette.
const C = {
  green: '#185451',        // Deep Teal — primary / brand
  greenDeep: '#134e4a',    //   darker teal (badge text, hovers)
  greenPillBg: '#D7F0EC',  //   light teal tint (pills)
  blue: '#5E6366',         // remapped -> Metallic Charcoal (no blue)
  amber: '#9FA8A3',        // remapped -> Sage Grey (no amber)
  ink: '#41464A',          // charcoal — headings
  ink2: '#33373A',         // charcoal — strongest headings
  gray: '#5E6366',         // Metallic Charcoal — body / secondary text
  grayMute: '#6E7679',     //   muted charcoal
  grayFaint: '#9FA8A3',    // Sage Grey — faint text / icons
  line: '#E7EBEA',         // light sage — borders / dividers
  card1: '#EAF3EC',        // light teal tint card
  card2: '#F1F4F3',        // light sage tint card
  card3: '#ECEFEE',        // light neutral tint card
  verifierBg: '#F5F7F6',   // very light sage — section background
  ctaBg: '#3C4143',        // dark charcoal — CTA band
  ctaAccent: '#6EE7D8',    // soft teal — accent on dark / "from anywhere"
};

const FONT = "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";

// Hero splash banner. Tune HERO_OVERLAY's alpha (0 = full photo, 1 = solid black)
// to balance the photo against text legibility; HERO_MAX_H caps the banner height.
const HERO_IMAGE = '/images/splash.webp';
const HERO_OVERLAY = 'rgba(0, 0, 0, 0.45)';
const HERO_MAX_H = 800;

const Logo = ({ h = 34, variant = 'dark', full = false }) => {
  const base = full ? '/images/logo-eqaya-inc' : '/images/logo-eqaya-mark';
  const src = `${base}${variant === 'light' ? '-light' : ''}.png`;
  return (
    <Box component="img" src={src} alt="Eqaya Inc." sx={{ height: h, width: 'auto', display: 'block' }} />
  );
};

const HomePage = () => {
  const navigate = useNavigate();

  const go = (path) => () => navigate(path);

  // Hero / card actions — wired to real public routes
  const actions = {
    manage: go('/login?role=client'),
    find: go('/contractors'),
    verify: go('/property-check/book'),
  };

  const cards = [
    {
      num: '01', bg: C.card1, accent: C.green, icon: <Assignment />,
      title: 'Manage your project',
      body: "Set milestones, track progress, and control payments — all from your phone, wherever you are in the world.",
      btn: 'Manage your project', btnIcon: <Add sx={{ fontSize: 17 }} />, onClick: actions.manage,
    },
    {
      num: '02', bg: C.card2, accent: C.blue, icon: <Engineering />,
      title: 'Find contractors',
      body: 'Browse identity-verified, rated contractors across Zimbabwe. Compare quotes and hire with confidence.',
      btn: 'Find contractors', btnIcon: <Search sx={{ fontSize: 17 }} />, onClick: actions.find,
    },
    {
      num: '03', bg: C.card3, accent: C.amber, icon: <VerifiedUser />,
      title: 'Verify work done',
      body: 'Hire an independent verifier — completely separate from your contractor — to physically confirm each stage.',
      btn: 'Verify work', btnIcon: <Check sx={{ fontSize: 17 }} />, onClick: actions.verify,
    },
  ];

  const verifierPoints = [
    'Verifiers are hired separately — no conflict of interest',
    'In-person site visits with dated photo evidence',
    'Signed report delivered to you before payment',
    'No verification = funds stay locked',
  ];

  const steps = [
    { badge: 'Step 1', badgeBg: C.greenPillBg, badgeFg: C.greenDeep, icon: <HomeOutlined />, iconColor: C.green, title: 'Post your project', body: 'Describe your build, set your budget and timeline' },
    { badge: 'Step 2', badgeBg: '#EEF1F0', badgeFg: '#5E6366', icon: <Engineering />, iconColor: C.blue, title: 'Hire a contractor', body: 'Compare quotes from verified local contractors' },
    { badge: 'Step 3', badgeBg: '#F1F4F3', badgeFg: '#6E7679', icon: <VerifiedUser />, iconColor: C.amber, title: 'Assign a verifier', body: 'Pick an independent verifier to inspect each stage' },
    { badge: 'Step 4', badgeBg: '#D7F0EC', badgeFg: C.greenDeep, icon: <CheckCircle />, iconColor: C.green, title: 'Approve and pay', body: 'Release funds only after the verifier signs off' },
  ];

  const footerGroups = [
    {
      title: 'Eqaya',
      links: [
        { label: 'About Us', path: '/about' },
        { label: 'Contact Us', path: '/contact' },
        { label: 'FAQ', path: '/faq' },
        { label: 'Privacy', path: '/privacy' },
      ],
    },
    {
      title: 'Services',
      links: [
        { label: 'Manage your project', path: '/login?role=client' },
        { label: 'Find contractors', path: '/contractors' },
        { label: 'Verify work', path: '/property-check/book' },
        { label: 'Apply as a contractor', path: '/register?role=contractor' },
      ],
    },
  ];

  const socialLinks = [
    { label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61590825145207' },
    { label: 'Instagram', href: 'https://www.instagram.com/eqayainc/' },
    { label: 'X', href: 'https://x.com/EqayaInc' },
  ];

  const heroBtnBase = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    px: '2rem', py: '0.9rem', borderRadius: '10px', minWidth: 160,
    fontFamily: FONT, fontSize: '0.95rem', fontWeight: 700, color: '#fff',
    cursor: 'pointer', border: 'none', textTransform: 'none', lineHeight: 1.2,
    transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', opacity: 0.92 },
    '&:focus-visible': { outline: `3px solid ${C.green}`, outlineOffset: 2 },
  };

  const cardBtnSx = (accent) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px', mt: '1.75rem',
    px: '1.25rem', py: '0.6rem', borderRadius: '8px', width: 'fit-content',
    fontFamily: FONT, fontSize: '0.85rem', fontWeight: 700, color: '#fff',
    bgcolor: accent, border: 'none', cursor: 'pointer', textTransform: 'none',
    transition: 'transform 0.15s, opacity 0.15s',
    '&:hover': { transform: 'translateY(-1px)', opacity: 0.92 },
    '&:focus-visible': { outline: `3px solid ${accent}`, outlineOffset: 2 },
  });

  return (
    <Box sx={{ fontFamily: FONT, bgcolor: '#fff', color: C.ink, lineHeight: 1.5 }}>
      <Seo canonicalPath="/" />

      {/* ── NAV (shared teal header) ── */}
      <PublicHeader />

      {/* ── HERO ── */}
      <Box component="section" sx={{
        position: 'relative', overflow: 'hidden',
        px: 'clamp(1.25rem,4vw,3rem)',
        py: { xs: 'clamp(2rem,6vw,3rem)', md: 0 },
        height: { md: HERO_MAX_H },        // hard ~500px banner on desktop
        minHeight: { xs: 400 },            // mobile grows with content
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundImage: `url(${HERO_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        {/* opacity overlay sitting between the splash photo and the text — tune HERO_OVERLAY */}
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, bgcolor: HERO_OVERLAY, zIndex: 0 }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1100, mx: 'auto', textAlign: 'center' }}>
        <Box sx={{
          display: 'inline-block', maxWidth: '100%',
          px: { xs: '1.25rem', md: '2.75rem' }, py: { xs: '0.75rem', md: '0.75rem' },
        }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          bgcolor: C.greenPillBg, color: C.greenDeep,
          fontSize: '0.78rem', fontWeight: 600, px: '0.9rem', py: '0.35rem',
          borderRadius: '100px', mb: '1rem',
        }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: C.green }} />
          For the Zimbabwean diaspora
        </Box>

        <Box component="h1" sx={{
          fontFamily: FONT, fontWeight: 800,
          fontSize: 'clamp(2.4rem,5vw,4.2rem)', lineHeight: 1.15,
          letterSpacing: '-0.04em', color: '#fff', mb: '1.1rem', m: 0,
          textShadow: '0 2px 18px rgba(0,0,0,0.45)',
        }}>
          <Box component="span" sx={{ whiteSpace: { sm: 'nowrap' } }}>Build your home in Zimbabwe,</Box>
          <br />
          <Box component="span" sx={{ color: C.ctaAccent }}>from anywhere</Box>
        </Box>

        <Box component="p" sx={{
          fontSize: 'clamp(0.95rem,1.4vw,1.1rem)', color: 'rgba(255,255,255,0.92)',
          maxWidth: 620, mx: 'auto', mt: '0.85rem', mb: '2rem', fontWeight: 600, lineHeight: 1.65,
          textShadow: '0 1px 10px rgba(0,0,0,0.5)',
        }}>
          From full builds to renovations and repairs — hire verified contractors, get independent
          eyes on the ground, and pay only when work is{' '}
          <Box component="strong" sx={{ color: '#fff', fontWeight: 800 }}>confirmed done</Box>
          . All from your phone in Johannesburg, Toronto, London, Sydney or New York.
        </Box>

        <Box sx={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', mb: '1rem' }}>
          <Box component="button" onClick={actions.manage} sx={{ ...heroBtnBase, bgcolor: C.green }}>
            Manage your project
          </Box>
          <Box component="button" onClick={actions.find} sx={{ ...heroBtnBase, bgcolor: 'rgba(255,255,255,0.14)', border: '1.5px solid rgba(255,255,255,0.7)' }}>
            Find contractors
          </Box>
          <Box component="button" onClick={actions.verify} sx={{ ...heroBtnBase, bgcolor: 'rgba(255,255,255,0.14)', border: '1.5px solid rgba(255,255,255,0.7)' }}>
            Verify work
          </Box>
        </Box>

        <Box component="p" sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.88)', fontWeight: 600, textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
          No travel needed&nbsp;·&nbsp;Photo proof every milestone&nbsp;·&nbsp;Free to get started
        </Box>

        <Box sx={{ mt: '1.25rem', display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Box
            component="button"
            onClick={go('/for-contractors')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.45)',
              color: '#fff', px: '1rem', py: '0.5rem', borderRadius: '100px',
              fontFamily: FONT, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
              '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 2 },
            }}
          >
            <Engineering sx={{ fontSize: 16 }} /> Are you a contractor? Apply to join &rarr;
          </Box>
          <Box
            component="button"
            onClick={go('/for-verifiers')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.45)',
              color: '#fff', px: '1rem', py: '0.5rem', borderRadius: '100px',
              fontFamily: FONT, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
              '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 2 },
            }}
          >
            <VerifiedUser sx={{ fontSize: 16 }} /> Want to verify work? Apply &rarr;
          </Box>
        </Box>
        </Box>
        </Box>
      </Box>

      {/* ── THREE CARDS ── */}
      <Box component="section" sx={{
        px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)', maxWidth: 1400, mx: 'auto',
      }}>
        <SectionLabel>Three things we do</SectionLabel>
        <SectionH2>
          <Box component="span" sx={{ whiteSpace: { md: 'nowrap' } }}>Everything you need. Nothing you don't.</Box>
        </SectionH2>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' },
          gap: { xs: '1.25rem', xl: '1.75rem' },
        }}>
          {cards.map((c) => (
            <Box key={c.num} sx={{
              position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column',
              bgcolor: c.bg, borderRadius: '16px', p: { xs: 'clamp(1.5rem,2.5vw,2.25rem)', xl: '2.5rem' },
            }}>
              <Box sx={{ position: 'absolute', top: '1.5rem', right: '1.75rem', fontSize: '3rem', fontWeight: 800, opacity: 0.07, color: '#000', lineHeight: 1 }}>
                {c.num}
              </Box>
              <Box sx={{
                width: 52, height: 52, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: c.accent, color: '#fff', mb: '1.25rem', '& .MuiSvgIcon-root': { fontSize: 26 },
              }}>
                {c.icon}
              </Box>
              <Box component="h3" sx={{ fontFamily: FONT, fontSize: 'clamp(1rem,1.5vw,1.2rem)', fontWeight: 700, color: C.ink2, mb: '0.5rem', m: 0 }}>
                {c.title}
              </Box>
              <Box component="p" sx={{ fontSize: 'clamp(0.82rem,1vw,0.9rem)', color: C.gray, lineHeight: 1.65, flex: 1, mt: '0.5rem' }}>
                {c.body}
              </Box>
              <Box component="button" onClick={c.onClick} sx={cardBtnSx(c.accent)}>
                {c.btnIcon}{c.btn}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── THE EQAYA GUARANTEE ── the wallet is our strongest feature; say it
          in one line where every visitor sees it, instead of burying it in
          "project wallet" mechanics further down. */}
      <Box component="section" sx={{
        borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}`,
        px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(2rem,3.5vw,2.75rem)', bgcolor: C.greenDeep, color: '#fff',
      }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: '1.25rem', md: '3rem' } }}>
          <Box sx={{ flex: '1 1 380px' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6EE7D8', mb: '0.5rem' }}>
              <Lock sx={{ fontSize: 16 }} /> The Eqaya Guarantee
            </Box>
            <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(1.35rem,2.4vw,1.85rem)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, m: 0, mb: '0.5rem' }}>
              Not a cent reaches a contractor until you&apos;ve seen the work and approved it.
            </Box>
            <Box component="p" sx={{ m: 0, fontSize: '0.95rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.82)', maxWidth: 560 }}>
              Your deposit sits in your own Eqaya wallet. Each stage is released only when you approve it,
              and a disputed stage stays locked until it&apos;s resolved. Zimbabwe&apos;s builds, diaspora&apos;s money — protected.
            </Box>
          </Box>
          <Box sx={{ flex: '1 1 320px', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: '0.6rem', width: '100%' }}>
            {[
              { n: '1', t: 'Deposit', b: 'into your wallet — it stays yours' },
              { n: '2', t: 'Work is done', b: 'and verified on the ground' },
              { n: '3', t: 'You approve', b: 'then, and only then, we pay' },
            ].map((s) => (
              <Box key={s.n} sx={{ bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '12px', px: '0.9rem', py: '0.75rem' }}>
                <Box sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#6EE7D8', mb: '2px' }}>STEP {s.n}</Box>
                <Box sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{s.t}</Box>
                <Box sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.4 }}>{s.b}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── TRUST PLATFORM BAND ── */}
      <Box component="section" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(2.5rem,4.5vw,4rem)', bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.green, mb: '0.6rem' }}>
            Building while you&apos;re away
          </Box>
          <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(1.7rem,3.2vw,2.4rem)', fontWeight: 800, letterSpacing: '-0.03em', color: C.ink, lineHeight: 1.15, m: 0, mb: '0.85rem' }}>
            Eqaya is a trust platform
          </Box>
          <Box component="p" sx={{ color: '#5E6366', maxWidth: 680, mx: 'auto', mb: '1.75rem', fontSize: '1rem', lineHeight: 1.65 }}>
            Eqaya is a trust platform for diasporans building, renovating and repairing property while
            you are away. Verified contractors do the work. Independent verifiers inspect it, with GPS
            proof and photos. You release payment from your wallet when satisfied.
          </Box>
          <Box sx={{ display: 'flex', gap: '0.9rem', justifyContent: 'center', flexWrap: 'wrap', mb: '1.75rem' }}>
            {[
              { icon: <VerifiedUser sx={{ fontSize: 18 }} />, label: 'Verified contractors do the work' },
              { icon: <PhotoCamera sx={{ fontSize: 18 }} />, label: 'Independent inspections — GPS proof & photos' },
              { icon: <Lock sx={{ fontSize: 18 }} />, label: 'You release payment when satisfied' },
            ].map((f) => (
              <Box key={f.label} sx={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                bgcolor: '#EAF3EC', color: C.green, fontWeight: 700, fontSize: '0.88rem',
                px: '1rem', py: '0.55rem', borderRadius: '100px',
              }}>
                {f.icon} {f.label}
              </Box>
            ))}
          </Box>
          <Box
            component="button"
            onClick={go('/trust')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '8px', px: '1.9rem', py: '0.85rem',
              borderRadius: '10px', fontFamily: FONT, fontSize: '0.98rem', fontWeight: 700,
              color: C.green, bgcolor: 'transparent', border: `2px solid ${C.green}`, cursor: 'pointer',
              textTransform: 'none', transition: 'background 0.15s',
              '&:hover': { bgcolor: 'rgba(24,84,81,0.06)' },
            }}
          >
            See how Eqaya protects you
          </Box>
        </Box>
      </Box>

      {/* ── VERIFIER SECTION ── */}
      <Box component="section" sx={{ bgcolor: C.verifierBg, px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)' }}>
        <Box sx={{
          maxWidth: 1400, mx: 'auto', display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 'clamp(2rem,5vw,5rem)', alignItems: 'center',
        }}>
          <Box>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              bgcolor: C.card3, color: '#6E7679', fontSize: '0.75rem', fontWeight: 700,
              px: '0.85rem', py: '0.3rem', borderRadius: '100px', mb: '1rem',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              The Eqaya difference
            </Box>
            <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(1.6rem,2.8vw,2.2rem)', fontWeight: 800, letterSpacing: '-0.03em', color: C.ink2, mb: '0.85rem', lineHeight: 1.2, m: 0 }}>
              Your contractor says<br />it's done. But is it?
            </Box>
            <Box component="p" sx={{ fontSize: 'clamp(0.85rem,1.1vw,0.95rem)', color: C.gray, lineHeight: 1.75, mb: '1.5rem', mt: '0.85rem' }}>
              When you're thousands of miles away, trust is everything. Our independent verifiers have zero relationship with your contractor. They visit the site, photograph every detail, and report only to you — before you release a single dollar.
            </Box>
            {verifierPoints.map((p) => (
              <Box key={p} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', mb: '0.75rem', fontSize: 'clamp(0.82rem,1vw,0.9rem)', color: '#444' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, mt: '1px' }}>
                  <Check sx={{ fontSize: 13 }} />
                </Box>
                <span>{p}</span>
              </Box>
            ))}
          </Box>

          <Box>
            <VCard
              initials="TM" name="Tendai Moyo" role="Independent Verifier · Harare"
              reportTitle="Site report · Borrowdale · 24 May 2026"
              reportBody="Roofing structure complete and matches approved plans. Timber quality acceptable. Ridge beam secured. Guttering 60% installed — contractor to complete by 28 May."
              photos
            />
            <VCard
              initials="CN" avBg="#EEF1F0" avFg="#5E6366" name="Chipo Ndlovu" role="Independent Verifier · Bulawayo"
              reportTitle="Foundation inspection · Suburbs · 21 May 2026"
              reportBody="Concrete pour complete. Depth and mix consistent with spec. Curing in progress — ready for wall plate in 7 days."
            />
          </Box>
        </Box>
      </Box>

      {/* ── HOW IT WORKS ── */}
      <Box component="section" id="how-it-works" sx={{ px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)', maxWidth: 1400, mx: 'auto', scrollMarginTop: '80px' }}>
        <SectionLabel>How it works</SectionLabel>
        <SectionH2>Up and running in four steps</SectionH2>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4,1fr)' },
          border: `1.5px solid ${C.line}`, borderRadius: '16px', overflow: 'hidden', mt: '2rem',
        }}>
          {steps.map((s, i) => (
            <Box key={s.badge} sx={{
              p: { xs: 'clamp(1.25rem,2vw,2rem) clamp(1rem,1.5vw,1.5rem)', xl: '2.25rem 2rem' },
              bgcolor: '#fff',
              borderRight: { md: i < 3 ? `1.5px solid ${C.line}` : 'none' },
              borderTop: { xs: i > 0 ? `1.5px solid ${C.line}` : 'none', sm: i > 1 ? `1.5px solid ${C.line}` : 'none', md: 'none' },
            }}>
              <Box sx={{
                display: 'inline-block', fontSize: '0.7rem', fontWeight: 700,
                px: '0.55rem', py: '0.2rem', borderRadius: '100px', mb: '1rem',
                letterSpacing: '0.04em', bgcolor: s.badgeBg, color: s.badgeFg,
              }}>
                {s.badge}
              </Box>
              <Box sx={{ mb: '0.85rem', color: s.iconColor, '& .MuiSvgIcon-root': { fontSize: 30 } }}>{s.icon}</Box>
              <Box component="h4" sx={{ fontFamily: FONT, fontSize: 'clamp(0.85rem,1vw,0.95rem)', fontWeight: 700, color: C.ink2, mb: '0.35rem', m: 0 }}>{s.title}</Box>
              <Box component="p" sx={{ fontSize: 'clamp(0.75rem,0.9vw,0.82rem)', color: C.grayFaint, lineHeight: 1.55, mt: '0.35rem' }}>{s.body}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── FOR CONTRACTORS (builder zone) ── */}
      <Box component="section" sx={{
        position: 'relative', overflow: 'hidden', color: '#fff',
        px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3rem,5vw,5rem)',
        background: `radial-gradient(900px 480px at 80% -20%, #1f6f6a 0%, ${C.green} 45%, ${C.greenDeep} 100%)`,
      }}>
        <Box aria-hidden sx={{
          position: 'absolute', inset: 0, opacity: 0.06, zIndex: 0,
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '46px 46px',
        }} />
        <Box sx={{
          position: 'relative', zIndex: 1, maxWidth: 1400, mx: 'auto',
          display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 'clamp(2rem,5vw,4rem)', alignItems: 'center',
        }}>
          <Box>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: '7px',
              bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
              fontSize: '0.75rem', fontWeight: 700, px: '0.85rem', py: '0.35rem', borderRadius: '100px', mb: '1rem',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <Engineering sx={{ fontSize: 15 }} /> For builders &amp; contractors
            </Box>
            <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(1.7rem,3vw,2.4rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.18, m: 0, mb: '0.85rem' }}>
              Are you a builder in Zimbabwe?<br /><Box component="span" sx={{ color: C.ctaAccent }}>Join our verified network.</Box>
            </Box>
            <Box component="p" sx={{ fontSize: 'clamp(0.9rem,1.1vw,1rem)', color: 'rgba(255,255,255,0.88)', lineHeight: 1.7, mb: '1.75rem', mt: 0, maxWidth: 520 }}>
              Apply once — we verify your business, portfolio and references — then you start getting matched with diaspora-funded projects and paid for every milestone you complete.
            </Box>
            <Box sx={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap' }}>
              <Box component="button" onClick={go('/register?role=contractor')} sx={{
                display: 'inline-flex', alignItems: 'center', gap: '7px', bgcolor: '#fff', border: 'none', color: C.greenDeep,
                px: '1.85rem', py: '0.85rem', borderRadius: '10px', fontFamily: FONT, fontSize: '0.98rem', fontWeight: 700, cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.25)' },
                '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 2 },
              }}>
                Apply to become a verified contractor
              </Box>
              <Box component="button" onClick={go('/for-contractors')} sx={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', bgcolor: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(255,255,255,0.55)', color: '#fff',
                px: '1.5rem', py: '0.85rem', borderRadius: '10px', fontFamily: FONT, fontSize: '0.98rem', fontWeight: 700, cursor: 'pointer',
                transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 2 },
              }}>
                How it works
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: '0.85rem' }}>
            {[
              { icon: <Engineering />, title: 'Diaspora-funded projects', body: 'Win serious, funded builds from clients abroad — not tyre-kickers.' },
              { icon: <Lock />, title: 'Paid for every milestone', body: 'Funds are secured up front and released as each stage is verified.' },
              { icon: <VerifiedUser />, title: 'A verified badge clients trust', body: 'Stand out with a profile diaspora clients can hire with confidence.' },
            ].map((b) => (
              <Box key={b.title} sx={{
                display: 'flex', alignItems: 'flex-start', gap: '14px',
                bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)',
                borderRadius: '14px', p: '1.1rem 1.25rem',
              }}>
                <Box sx={{ width: 42, height: 42, borderRadius: '11px', flexShrink: 0, bgcolor: 'rgba(255,255,255,0.14)', color: C.ctaAccent, display: 'flex', alignItems: 'center', justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: 22 } }}>
                  {b.icon}
                </Box>
                <Box>
                  <Box sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#fff', mb: '0.15rem' }}>{b.title}</Box>
                  <Box sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.55 }}>{b.body}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box component="section" sx={{ bgcolor: C.ctaBg, px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(3.5rem,6vw,6rem)', textAlign: 'center' }}>
        <Box component="h2" sx={{ fontFamily: FONT, fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em', mb: '0.75rem', lineHeight: 1.15, m: 0 }}>
          Start building<br /><Box component="span" sx={{ color: C.ctaAccent }}>back home today</Box>
        </Box>
        <Box component="p" sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', maxWidth: 420, mx: 'auto', mb: '2rem', mt: '0.75rem', lineHeight: 1.65 }}>
          Join thousands of diaspora families building with confidence, transparency, and real peace of mind.
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Box component="button" onClick={go('/start')} sx={{
            bgcolor: C.green, border: 'none', color: '#fff', px: '2.5rem', py: '0.9rem', borderRadius: '10px',
            fontFamily: FONT, fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
            transition: 'transform 0.15s, opacity 0.15s', '&:hover': { transform: 'translateY(-2px)', opacity: 0.92 },
            '&:focus-visible': { outline: `3px solid ${C.ctaAccent}`, outlineOffset: 2 },
          }}>
            Get started free
          </Box>
          <Box component="button" onClick={go('/register?role=contractor')} sx={{
            bgcolor: 'transparent', border: `1.5px solid ${C.ctaAccent}`, color: C.ctaAccent,
            px: '2rem', py: '0.9rem', borderRadius: '10px', fontFamily: FONT, fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
            transition: 'border-color 0.15s, color 0.15s',
            '&:hover': { bgcolor: `${C.ctaAccent}18`, borderColor: C.ctaAccent, color: '#fff' },
            '&:focus-visible': { outline: `3px solid ${C.ctaAccent}`, outlineOffset: 2 },
          }}>
            Apply to become a verified contractor
          </Box>
          <Box component="button" onClick={go('/what-is-eqaya')} sx={{
            bgcolor: 'transparent', border: '1.5px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)',
            px: '2rem', py: '0.9rem', borderRadius: '10px', fontFamily: FONT, fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
            '&:hover': { borderColor: 'rgba(255,255,255,0.45)', color: '#fff' },
            '&:focus-visible': { outline: '3px solid rgba(255,255,255,0.6)', outlineOffset: 2 },
          }}>
            Talk to us first
          </Box>
        </Box>
      </Box>

      {/* ── FOOTER ── */}
      <Box component="footer" sx={{
        px: 'clamp(1.25rem,4vw,3rem)', py: 'clamp(2.5rem,5vw,4rem)', borderTop: `1px solid ${C.line}`,
        bgcolor: '#fff',
      }}>
        <Box sx={{
          maxWidth: 1200, mx: 'auto', display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1fr 1fr' },
          gap: { xs: '2rem', md: '3rem' }, alignItems: 'start',
        }}>
          <Box>
            <Box component="a" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} sx={{ textDecoration: 'none', display: 'inline-block', mb: 1.5 }}>
              <Logo full h={84} />
            </Box>
            <Box sx={{ fontSize: '0.88rem', color: C.gray, lineHeight: 1.7, maxWidth: 320 }}>
              Build, verify, and manage construction work in Zimbabwe from anywhere.
            </Box>
          </Box>

          {footerGroups.map((group) => (
            <Box key={group.title}>
              <Box sx={{ fontSize: '0.88rem', fontWeight: 800, color: C.green, mb: 1.25 }}>
                {group.title}
              </Box>
              {group.links.map((link) => (
                <Box
                  key={link.label}
                  component="button"
                  onClick={go(link.path)}
                  sx={{
                    display: 'block', p: 0, mb: 0.85, border: 0, bgcolor: 'transparent',
                    fontFamily: FONT, color: C.gray, fontSize: '0.9rem', cursor: 'pointer', textAlign: 'left',
                    '&:hover': { color: C.green },
                  }}
                >
                  {link.label}
                </Box>
              ))}
            </Box>
          ))}

          <Box>
            <Box sx={{ fontSize: '0.88rem', fontWeight: 800, color: C.green, mb: 1.25 }}>
              Social media
            </Box>
            {socialLinks.map((link) => (
              <Box
                key={link.label}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block', mb: 0.85, color: C.gray, fontSize: '0.9rem',
                  textDecoration: 'none', '&:hover': { color: C.green },
                }}
              >
                {link.label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{
          maxWidth: 1200, mx: 'auto', mt: 3, pt: 2, borderTop: `1px solid ${C.line}`,
          fontSize: '0.78rem', color: '#9a9a9a',
        }}>
          © 2026 Eqaya · Built for the Zimbabwean diaspora
        </Box>
      </Box>
    </Box>
  );
};

/* ── Shared bits ── */
const SectionLabel = ({ children }) => (
  <Box sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.green, mb: '0.6rem', textAlign: 'center' }}>
    {children}
  </Box>
);

const SectionH2 = ({ children }) => (
  <Box sx={{ fontFamily: FONT, fontSize: 'clamp(1.8rem,3.5vw,2.6rem)', fontWeight: 800, letterSpacing: '-0.03em', color: C.ink2, lineHeight: 1.15, textAlign: 'center', mb: '2.5rem' }}>
    {children}
  </Box>
);

const VCard = ({ initials, avBg = C.greenPillBg, avFg = C.greenDeep, name, role, reportTitle, reportBody, photos = false }) => (
  <Box sx={{ bgcolor: '#fff', borderRadius: '14px', p: '1.25rem 1.5rem', border: `1.5px solid ${C.line}`, mb: '1rem', '&:last-child': { mb: 0 } }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '1rem', flexWrap: 'wrap' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: avBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: avFg, flexShrink: 0 }}>
        {initials}
      </Box>
      <Box>
        <Box sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.ink2 }}>{name}</Box>
        <Box sx={{ fontSize: '0.75rem', color: '#888' }}>{role}</Box>
      </Box>
      <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: C.greenPillBg, color: C.greenDeep, fontSize: '0.68rem', fontWeight: 700, px: '0.6rem', py: '0.2rem', borderRadius: '100px' }}>
        Verified <Check sx={{ fontSize: 12 }} />
      </Box>
    </Box>
    <Box sx={{ bgcolor: C.verifierBg, borderLeft: `3px solid ${C.green}`, p: '0.85rem 1rem', borderRadius: '0 8px 8px 0', fontSize: '0.8rem', color: C.gray, lineHeight: 1.6 }}>
      <Box component="strong" sx={{ color: C.ink2, fontWeight: 600, display: 'block', mb: '0.25rem' }}>{reportTitle}</Box>
      {reportBody}
    </Box>
    {photos && (
      <Box sx={{ display: 'flex', gap: '6px', mt: '0.85rem', flexWrap: 'wrap' }}>
        {['1', '2', '3', '+11'].map((p) => (
          <Box key={p} sx={{ width: 44, height: 34, bgcolor: '#f0f0f0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', fontSize: '0.7rem', color: '#999' }}>
            {p.startsWith('+') ? p : <><PhotoCamera sx={{ fontSize: 12 }} />{p}</>}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

/* ── Product mock: a compact "live project" dashboard shown under the hero ── */
const ProductMock = () => (
  <Box sx={{
    maxWidth: 860, mx: 'auto', mt: 'clamp(2.5rem,5vw,4rem)', textAlign: 'left',
    border: `1px solid ${C.line}`, borderRadius: '18px', bgcolor: '#fff', overflow: 'hidden',
    boxShadow: '0 30px 60px -25px rgba(8,80,65,0.28), 0 12px 24px -12px rgba(0,0,0,0.10)',
  }}>
    {/* window chrome */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', px: '1rem', py: '0.7rem', borderBottom: `1px solid ${C.line}`, bgcolor: '#fafafa' }}>
      {['#ff5f57', '#febc2e', '#28c840'].map((c) => (<Box key={c} sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: c }} />))}
      <Box sx={{ ml: '0.5rem', fontSize: '0.72rem', color: '#999', fontWeight: 500 }}>app.eqaya.com · Borrowdale Build</Box>
    </Box>

    <Box sx={{ p: { xs: '1.1rem', sm: '1.5rem' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', mb: '1rem' }}>
        <Box>
          <Box sx={{ fontSize: '0.95rem', fontWeight: 800, color: C.ink2 }}>Milestone 3 of 6 · Roofing</Box>
          <Box sx={{ fontSize: '0.78rem', color: '#888' }}>4-bed house · Harare</Box>
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', bgcolor: C.card3, color: '#6E7679', fontSize: '0.72rem', fontWeight: 700, px: '0.7rem', py: '0.3rem', borderRadius: '100px' }}>
          <Lock sx={{ fontSize: 13 }} /> Funds locked until verified
        </Box>
      </Box>

      {/* progress */}
      <Box sx={{ height: 8, borderRadius: '100px', bgcolor: '#eef2f0', overflow: 'hidden', mb: '0.4rem' }}>
        <Box sx={{ width: '52%', height: '100%', borderRadius: '100px', background: `linear-gradient(90deg, ${C.green}, ${C.ctaAccent})` }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#999', mb: '1.25rem' }}>
        <span>52% complete</span><span>$8,400 of $16,000 released</span>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.3fr 1fr' }, gap: '0.85rem' }}>
        {/* verifier report */}
        <Box sx={{ bgcolor: C.verifierBg, borderLeft: `3px solid ${C.green}`, p: '0.85rem 1rem', borderRadius: '0 10px 10px 0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '0.5rem' }}>
            <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: C.greenPillBg, color: C.greenDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 700 }}>TM</Box>
            <Box sx={{ fontSize: '0.76rem', fontWeight: 700, color: C.ink2 }}>Tendai · Verifier</Box>
            <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', color: C.green }}><VerifiedUser sx={{ fontSize: 15 }} /></Box>
          </Box>
          <Box sx={{ fontSize: '0.76rem', color: C.gray, lineHeight: 1.55 }}>
            Roof structure matches approved plans. Timber quality good. Ridge beam secured.
          </Box>
          <Box sx={{ display: 'flex', gap: '5px', mt: '0.65rem' }}>
            {['1', '2', '3'].map((p) => (
              <Box key={p} sx={{ width: 38, height: 28, bgcolor: '#e8efeb', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9FA8A3' }}>
                <PhotoCamera sx={{ fontSize: 13 }} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* approve action */}
        <Box sx={{ border: `1px solid ${C.line}`, borderRadius: '10px', p: '0.85rem 1rem', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.6rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.78rem', fontWeight: 600, color: C.ink2 }}>
            <CheckCircle sx={{ fontSize: 17, color: C.green }} /> Verifier signed off
          </Box>
          <Box sx={{ bgcolor: C.green, color: '#fff', textAlign: 'center', fontSize: '0.8rem', fontWeight: 700, py: '0.55rem', borderRadius: '8px' }}>
            Release $2,600
          </Box>
          <Box sx={{ fontSize: '0.68rem', color: '#999', textAlign: 'center' }}>You approve every payment</Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default HomePage;
